import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import AuthImage from "@/components/AuthImage";
import { Input } from "@/components/ui/input";
import { PlusCircle, Trash, MagnifyingGlass, ChartBar, Sliders } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminComparison() {
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [scores, setScores] = useState({}); // { product_id: { cat_id: val } }
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingCats, setSavingCats] = useState(false);
  const [savingScores, setSavingScores] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, s] = await Promise.all([
        api.get("/comparison/categories"),
        api.get("/products?include_inactive=true"),
        api.get("/comparison/scores"),
      ]);
      setCats((c.data || []).map((x) => ({ category_id: x.category_id, name: x.name, weight: x.weight })));
      setProducts(p.data || []);
      setScores(s.data || {});
    } catch (e) {
      toast.error("Failed to load comparison data");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const total = useMemo(
    () => Math.round(cats.reduce((sum, c) => sum + (parseFloat(c.weight) || 0), 0) * 100) / 100,
    [cats]
  );
  const balanced = cats.length === 0 || total === 100;

  const saveCats = async () => {
    if (!balanced) {
      toast.error(`Weights must sum to 100% (currently ${total}%)`);
      return;
    }
    if (cats.some((c) => !(c.name || "").trim())) {
      toast.error("Every category needs a name");
      return;
    }
    setSavingCats(true);
    try {
      const res = await api.put("/comparison/categories", {
        categories: cats.map((c) => ({
          category_id: c.category_id || null,
          name: c.name.trim(),
          weight: parseFloat(c.weight) || 0,
        })),
      });
      setCats((res.data || []).map((x) => ({ category_id: x.category_id, name: x.name, weight: x.weight })));
      toast.success("Comparison categories saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save categories");
    }
    setSavingCats(false);
  };

  const setScore = (pid, cid, raw) => {
    let v = raw.replace(/[^0-9.]/g, "");
    setScores((prev) => ({ ...prev, [pid]: { ...(prev[pid] || {}), [cid]: v } }));
  };

  const saveScores = async () => {
    const savedCatIds = cats.filter((c) => c.category_id).map((c) => c.category_id);
    if (savedCatIds.length === 0) {
      toast.error("Save at least one category first");
      return;
    }
    setSavingScores(true);
    const payload = [];
    products.forEach((p) => {
      savedCatIds.forEach((cid) => {
        const raw = scores[p.product_id]?.[cid];
        if (raw !== undefined && raw !== "") {
          let v = Math.max(0, Math.min(10, parseFloat(raw) || 0));
          payload.push({ product_id: p.product_id, category_id: cid, score: v });
        }
      });
    });
    try {
      await api.put("/comparison/scores", { scores: payload });
      toast.success(`Saved ${payload.length} scores`);
    } catch (e) {
      toast.error("Failed to save scores");
    }
    setSavingScores(false);
  };

  const savedCats = cats.filter((c) => c.category_id);
  const filtered = products.filter((p) =>
    (p.name || "").toLowerCase().includes(q.toLowerCase()) || (p.brand || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Layout>
      <div className="max-w-5xl">
        <div className="flex items-center gap-2 mb-1">
          <ChartBar size={24} weight="duotone" className="text-[#0047AB]" />
          <h1 className="font-display text-3xl font-black tracking-tight text-zinc-900">Comparison scorecard</h1>
        </div>
        <p className="text-sm text-zinc-500 mb-8">
          Define weighted categories, then score each product 0–10. Weights must total 100%. Final score = Σ(score × weight ÷ 100).
        </p>

        {/* Categories manager */}
        <section className="bg-white border border-zinc-200 p-5 md:p-6 mb-8" data-testid="comparison-categories-manager">
          <div className="flex items-center gap-2 mb-4">
            <Sliders size={18} className="text-[#FF5A1F]" />
            <h2 className="font-display text-xl font-bold text-zinc-900">Categories & weights</h2>
          </div>

          <div className="space-y-2" data-testid="comparison-categories-list">
            {cats.map((c, i) => (
              <div key={c.category_id || `new-${i}`} className="flex items-center gap-2">
                <Input
                  value={c.name}
                  onChange={(e) => setCats((arr) => arr.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  placeholder="Durability"
                  data-testid={`comparison-cat-name-${i}`}
                  className="rounded-none h-11 border-zinc-200 flex-1"
                />
                <div className="flex items-center border border-zinc-200 h-11">
                  <input
                    value={c.weight}
                    onChange={(e) =>
                      setCats((arr) =>
                        arr.map((x, j) => (j === i ? { ...x, weight: e.target.value.replace(/[^0-9.]/g, "") } : x))
                      )
                    }
                    placeholder="0"
                    data-testid={`comparison-cat-weight-${i}`}
                    className="w-16 text-center outline-none h-full px-2 text-zinc-900"
                  />
                  <span className="px-2 text-zinc-400 text-sm">%</span>
                </div>
                <button
                  onClick={() => setCats((arr) => arr.filter((_, j) => j !== i))}
                  data-testid={`comparison-cat-remove-${i}`}
                  className="text-red-500 hover:bg-red-50 p-2.5"
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={() => setCats((arr) => [...arr, { category_id: null, name: "", weight: "" }])}
            data-testid="comparison-cat-add"
            className="mt-3 inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
          >
            <PlusCircle size={14} /> Add category
          </button>

          <div className="flex items-center justify-between mt-5 pt-4 border-t border-zinc-200">
            <div
              data-testid="comparison-weight-total"
              className={`text-sm font-bold ${balanced ? "text-emerald-600" : "text-red-500"}`}
            >
              Total weight: {total}% {balanced ? "✓" : "— must equal 100%"}
            </div>
            <button
              onClick={saveCats}
              disabled={savingCats || !balanced}
              data-testid="comparison-save-categories"
              className="bg-[#0047AB] hover:bg-[#003380] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-50"
            >
              {savingCats ? "Saving..." : "Save categories"}
            </button>
          </div>
        </section>

        {/* Scores spreadsheet */}
        <section className="bg-white border border-zinc-200 p-5 md:p-6" data-testid="comparison-scores-manager">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="font-display text-xl font-bold text-zinc-900">Product scores (0–10)</h2>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products"
                data-testid="comparison-product-search"
                className="rounded-none h-10 border-zinc-200 pl-9 w-56"
              />
            </div>
          </div>

          {savedCats.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">
              Save at least one category above to start scoring products.
            </div>
          ) : loading ? (
            <div className="h-40 bg-zinc-50 animate-pulse" />
          ) : (
            <div className="overflow-x-auto border border-zinc-100">
              <table className="w-full border-collapse" data-testid="comparison-scores-table">
                <thead>
                  <tr className="bg-zinc-50">
                    <th className="text-left mt-tiny text-zinc-500 font-mono-mt p-3 sticky left-0 bg-zinc-50 min-w-[200px]">
                      Product
                    </th>
                    {savedCats.map((c) => (
                      <th key={c.category_id} className="mt-tiny text-zinc-500 font-mono-mt p-3 text-center min-w-[110px]">
                        {c.name}
                        <div className="text-[10px] text-zinc-400 font-normal">{c.weight}%</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.product_id} className="border-t border-zinc-100" data-testid={`comparison-score-row-${p.product_id}`}>
                      <td className="p-3 sticky left-0 bg-white">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 bg-zinc-100 overflow-hidden shrink-0">
                            <AuthImage path={p.image_path} alt={p.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-zinc-900 truncate">{p.name}</div>
                            {p.brand && <div className="text-[11px] text-zinc-400 truncate">{p.brand}</div>}
                          </div>
                        </div>
                      </td>
                      {savedCats.map((c) => (
                        <td key={c.category_id} className="p-2 text-center">
                          <input
                            value={scores[p.product_id]?.[c.category_id] ?? ""}
                            onChange={(e) => setScore(p.product_id, c.category_id, e.target.value)}
                            placeholder="–"
                            data-testid={`comparison-score-${p.product_id}-${c.category_id}`}
                            className="w-14 text-center border border-zinc-200 h-9 outline-none focus:border-[#0047AB] text-zinc-900"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={savedCats.length + 1} className="p-8 text-center text-zinc-400 text-sm">
                        No products match "{q}".
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {savedCats.length > 0 && (
            <div className="flex justify-end mt-5">
              <button
                onClick={saveScores}
                disabled={savingScores}
                data-testid="comparison-save-scores"
                className="bg-[#0047AB] hover:bg-[#003380] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-50"
              >
                {savingScores ? "Saving..." : "Save all scores"}
              </button>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
